import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ScreenHeader } from '../components/ScreenHeader';
import { ScoreRing } from '../components/ScoreRing';
import { useAdminKpi } from '../hooks/useAdminKpi';
import { exportAdminPdf } from '../services/adminReportPdf';
import {
  AI_MODE_LABEL,
  WINDOW_LABEL,
  formatCost,
  formatEuros,
  formatTokens,
  fetchFeedbackComments,
  projectionMensuelle,
  type AdminKpi,
  type ApiCalls,
  type AdminWindow,
  type AiUsage,
  type AnalysisMetrics,
  type ErrorFreq,
  type FeedbackComment,
  type FeedbackTrendPoint,
  type LabeledCount,
  type ScoreProgression,
} from '../services/adminKpi';
import { REPERES_ADMIN } from '../config/reperesAdmin';
import { colors, radius, screenPadding, spacing, typography } from '../theme/tokens';

export interface AdminScreenProps {
  onBack?: () => void;
}

const WINDOWS: AdminWindow[] = ['current', '1m', '3m', '1y'];

// Barre de MAGNITUDE : une seule teinte (séquentiel), valeur toujours affichée en texte (jamais couleur seule).
function BarList({ items }: { items: LabeledCount[] }) {
  if (items.length === 0) return <Text style={styles.empty}>Aucune donnée.</Text>;
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <View style={styles.bars}>
      {items.map((it) => (
        <View key={it.label} style={styles.barRow}>
          <Text style={styles.barLabel} numberOfLines={1}>
            {it.label}
          </Text>
          <View style={styles.barTrack}>
            <View style={[styles.barFill, { width: `${Math.round((it.count / max) * 100)}%` }]} />
          </View>
          <Text style={styles.barValue}>{it.count}</Text>
        </View>
      ))}
    </View>
  );
}

function StatTile({ value, label }: { value: string | number; label: string }) {
  return (
    <View style={styles.tile}>
      <Text style={styles.tileValue}>{value}</Text>
      <Text style={styles.tileLabel}>{label}</Text>
    </View>
  );
}

function Section({ title, children, testID }: { title: string; children: React.ReactNode; testID?: string }) {
  return (
    <View style={styles.section} testID={testID}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SubLabel({ children }: { children: React.ReactNode }) {
  return <Text style={styles.subLabel}>{children}</Text>;
}

const FEATURE_LABEL: Record<string, string> = {
  cv_create: 'Création de CV',
  cv_score: 'Notation de CV',
  linkedin_audit: 'Audit LinkedIn',
};
const COMMENTS_PAGE = 50;

function groupTrend(trend: FeedbackTrendPoint[]): Record<string, FeedbackTrendPoint[]> {
  const out: Record<string, FeedbackTrendPoint[]> = {};
  for (const p of trend) (out[p.feature] ??= []).push(p);
  return out;
}

// Section Feedback : cartes par feature (satisfaction + volume), évolution hebdo, et commentaires
// paginés avec toggle Négatifs/Positifs. AUCUNE identité d'auteur (feature · date · note · texte).
function FeedbackSection({ data }: { data: AdminKpi['feedback'] }) {
  const [filter, setFilter] = useState<'down' | 'up'>('down');
  // Négatifs préchargés (recentNegative) ; positifs chargés à la demande (null = pas encore).
  const [neg, setNeg] = useState<FeedbackComment[]>(data.recentNegative);
  const [negMore, setNegMore] = useState(data.recentNegative.length >= COMMENTS_PAGE);
  const [pos, setPos] = useState<FeedbackComment[] | null>(null);
  const [posMore, setPosMore] = useState(true);
  const [loading, setLoading] = useState(false);

  // Reset quand les KPI changent (fenêtre / rechargement) : négatifs re-préchargés, positifs re-lazy.
  useEffect(() => {
    setNeg(data.recentNegative);
    setNegMore(data.recentNegative.length >= COMMENTS_PAGE);
    setPos(null);
    setPosMore(true);
  }, [data.recentNegative]);

  // 1re page des positifs, chargée paresseusement à la 1re bascule sur « Positifs ».
  useEffect(() => {
    if (filter !== 'up' || pos !== null) return;
    let alive = true;
    setLoading(true);
    fetchFeedbackComments('up', 0, COMMENTS_PAGE)
      .then((page) => {
        if (!alive) return;
        setPos(page);
        setPosMore(page.length >= COMMENTS_PAGE);
      })
      .catch(() => {
        if (alive) {
          setPos([]);
          setPosMore(false);
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [filter, pos]);

  const comments = filter === 'down' ? neg : pos ?? [];
  const hasMore = filter === 'down' ? negMore : posMore;

  const loadMore = async () => {
    setLoading(true);
    try {
      const next = await fetchFeedbackComments(filter, comments.length, COMMENTS_PAGE);
      if (filter === 'down') {
        setNeg((prev) => [...prev, ...next]);
        setNegMore(next.length >= COMMENTS_PAGE);
      } else {
        setPos((prev) => [...(prev ?? []), ...next]);
        setPosMore(next.length >= COMMENTS_PAGE);
      }
    } catch {
      if (filter === 'down') setNegMore(false);
      else setPosMore(false); // échec non bloquant : on arrête proprement le « charger plus »
    } finally {
      setLoading(false);
    }
  };

  const trendByFeature = groupTrend(data.trend);

  return (
    <Section title="Feedback" testID="admin-feedback">
      {data.byFeature.length === 0 ? (
        <Text style={styles.empty}>Aucun avis pour l’instant.</Text>
      ) : (
        data.byFeature.map((f) => (
          <View key={f.feature} testID={`admin-feedback-${f.feature}`}>
            <SubLabel>{FEATURE_LABEL[f.feature] ?? f.feature}</SubLabel>
            <View style={styles.tiles}>
              <StatTile value={`${f.satisfaction}%`} label="Satisfaction" />
              <StatTile value={f.total} label="Avis" />
              <StatTile value={`${f.positive} / ${f.negative}`} label="👍 / 👎" />
            </View>
            {trendByFeature[f.feature]?.length ? (
              <BarList
                items={trendByFeature[f.feature].map((p) => ({ label: p.week.slice(5), count: p.satisfaction }))}
              />
            ) : null}
          </View>
        ))
      )}

      <SubLabel>Commentaires récents</SubLabel>
      <View style={styles.segmented}>
        {(['down', 'up'] as const).map((r) => {
          const active = filter === r;
          return (
            <Pressable
              key={r}
              testID={`admin-feedback-filter-${r}`}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              onPress={() => setFilter(r)}
              style={[styles.segment, active && styles.segmentActive]}
            >
              <Text style={[styles.segmentText, active && styles.segmentTextActive]}>
                {r === 'down' ? 'Négatifs 👎' : 'Positifs 👍'}
              </Text>
            </Pressable>
          );
        })}
      </View>

      {loading && comments.length === 0 ? (
        <ActivityIndicator testID="admin-feedback-loading" color={colors.electric} style={styles.center} />
      ) : comments.length === 0 ? (
        <Text style={styles.empty}>
          {filter === 'down' ? 'Aucun commentaire négatif.' : 'Aucun commentaire positif.'}
        </Text>
      ) : (
        comments.map((c, i) => (
          <View key={`${c.createdAt}-${i}`} style={styles.errRow} testID={`admin-feedback-comment-${i}`}>
            <Text style={styles.errName}>
              {FEATURE_LABEL[c.feature] ?? c.feature} · {c.createdAt.slice(0, 10)} ·{' '}
              {c.rating === 'up' ? '👍' : '👎'}
            </Text>
            <Text style={styles.commentText}>{c.comment}</Text>
          </View>
        ))
      )}
      {hasMore && comments.length > 0 ? (
        <Pressable testID="admin-feedback-more" onPress={loadMore} disabled={loading} style={styles.moreBtn}>
          <Text style={styles.moreText}>{loading ? 'Chargement…' : 'Charger plus'}</Text>
        </Pressable>
      ) : null}
    </Section>
  );
}

// Codes normalisés → libellés lisibles (CV grille des 22 + LinkedIn provisoire).
const ERROR_LABELS: Record<string, string> = {
  no_metrics: 'Aucun résultat chiffré',
  job_description_style: 'Style fiche de poste',
  student_jobs_undervalued: 'Jobs étudiants non valorisés',
  weak_title: 'Titre faible ou absent',
  no_availability_rhythm: 'Dispo / rythme absents',
  unproven_soft_skills: 'Soft skills sans preuve',
  offer_copy_paste: 'Copier-coller de l’offre',
  empty_titles: 'Intitulés vides',
  date_gaps: 'Trous / chevauchements de dates',
  languages_below_b2: 'Langues sous B2',
  personal_data: 'Données perso (photo, âge…)',
  unprofessional_email: 'Email non professionnel',
  full_postal_address: 'Adresse postale complète',
  overrated_levels: 'Niveaux surévalués',
  two_pages_junior: 'Deux pages (junior)',
  non_ats_layout: 'Mise en page non ATS',
  spelling_errors: 'Fautes / accents',
  heterogeneous_dates: 'Dates hétérogènes',
  inconsistent_punctuation: 'Ponctuation incohérente',
  generic_interests: 'Centres d’intérêt génériques',
  no_skills_section: 'Pas de rubrique compétences',
  no_linkedin: 'LinkedIn absent',
  // LinkedIn (grille des 23)
  default_student_title: 'Titre par défaut « Étudiant »',
  seeking_only: '« En recherche » sans métier',
  incompatible_projects: 'Projets incompatibles affichés',
  job_not_in_first_40: 'Métier absent des 40 premiers car.',
  no_contract_location: 'Contrat / localisation / dispo absents',
  empty_about: 'Section Infos vide ou faible',
  about_copies_cv: 'Infos recopie le CV',
  experiences_no_description: 'Expériences sans description',
  tasks_not_achievements: 'Tâches sans réalisations ni chiffres',
  no_proof: 'Aucune preuve concrète',
  undemonstrated_buzzwords: 'Buzzwords non démontrés',
  education_no_detail: 'Formation sans détail',
  cv_linkedin_inconsistency: 'Incohérences CV / LinkedIn',
  bad_photo: 'Photo inadaptée',
  default_banner: 'Bannière par défaut',
  generic_skills: 'Compétences génériques',
  pinned_skills_off_target: 'Compétences épinglées hors métier',
  non_custom_url: 'URL non personnalisée',
  ghost_profile: 'Profil fantôme (aucune activité)',
  emoji_overload: 'Trop d’émojis / symboles',
  search_not_recruiters_only: 'Recherche pas en « recruteurs seuls »',
  public_opentowork_badge: 'Badge public #OpenToWork actif',
};

function ErrorFreqList({ items }: { items: ErrorFreq[] }) {
  if (items.length === 0) return <Text style={styles.empty}>Aucune donnée.</Text>;
  return (
    <>
      {items.map((e) => (
        <View key={e.code} style={styles.errRow}>
          <Text style={styles.errName}>{ERROR_LABELS[e.code] ?? e.code}</Text>
          <Text style={styles.errMeta}>
            {e.count} occ. · {e.pct}% des analyses
          </Text>
        </View>
      ))}
    </>
  );
}

function progressionText(p: ScoreProgression): string {
  if (p.users === 0) return 'n/a (pas assez de données)';
  const sign = p.avgDelta > 0 ? '+' : '';
  return `${sign}${p.avgDelta} pts · ${p.users} utilisateur${p.users > 1 ? 's' : ''}`;
}

const milliers = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

// Nos appels à Adzuna, rapportés à nos plafonds (sous les limites publiques d'Adzuna). Le mois est
// la fenêtre qui tombe en premier : la projection dit, au rythme actuel, quel jour il sera épuisé.
function ApiCallsSection({ data }: { data: ApiCalls }) {
  const a = data.adzuna;
  const { projection, jourAtteinte, part } = projectionMensuelle(a.mois, a.plafonds.mois, new Date());
  return (
    <Section title="Appels Adzuna" testID="admin-api-adzuna">
      <View style={styles.tiles}>
        <StatTile value={`${milliers(a.mois)} / ${milliers(a.plafonds.mois)}`} label={`Ce mois (${part} %)`} />
        <StatTile value={`${milliers(a.semaine)} / ${milliers(a.plafonds.semaine)}`} label="Cette semaine" />
        <StatTile value={`${milliers(a.jour)} / ${milliers(a.plafonds.jour)}`} label="Aujourd’hui" />
      </View>
      <Text style={styles.aiNote}>Projection fin de mois au rythme actuel : {milliers(projection)} appels.</Text>
      {jourAtteinte !== null ? (
        <Text style={styles.aiWarn} testID="admin-api-adzuna-alerte">
          Plafond mensuel atteint vers le {jourAtteinte} : au-delà, les recherches se font sans Adzuna.
        </Text>
      ) : null}
    </Section>
  );
}

// Consommation Anthropic. Le mot « estimé » est partout : ces montants ignorent la mise en cache
// des prompts, le batching et les remises contractuelles. Ce n'est pas une facture.
function AiUsageSection({ data }: { data: AiUsage }) {
  const cur = data.currency;
  const inputShare =
    data.total.cost > 0 ? Math.round((data.total.costInput / data.total.cost) * 100) : 0;

  return (
    <>
      <Section title={`Coût IA estimé (${cur})`} testID="admin-ai-usage">
        <View style={styles.tiles}>
          <StatTile value={formatCost(data.total.cost, cur)} label="Total estimé" />
          <StatTile value={formatTokens(data.total.inputTokens + data.total.outputTokens)} label="Tokens" />
          <StatTile value={data.total.calls} label="Appels" />
        </View>
        <View style={styles.tiles}>
          <StatTile value={formatCost(data.perAnalysis, cur)} label="Par analyse livrée" />
          <StatTile value={formatCost(data.perActiveUser, cur)} label="Par utilisateur actif" />
          <StatTile value={formatCost(data.perCall, cur)} label="Par appel" />
        </View>
        <Text style={styles.aiNote} testID="admin-ai-estimate-note">
          Estimation, pas une facture : la mise en cache des prompts, le batching et les remises
          contractuelles ne sont pas mesurés ici.
        </Text>
        {data.pricingMissing.length > 0 ? (
          <Text style={styles.aiWarn} testID="admin-ai-pricing-missing">
            Aucun tarif saisi pour {data.pricingMissing.join(', ')} : le total est sous-estimé.
          </Text>
        ) : null}
      </Section>

      <Section title="Entrée / sortie" testID="admin-ai-split">
        <View style={styles.errRow}>
          <Text style={styles.errName}>Entrée · {formatTokens(data.total.inputTokens)} tokens</Text>
          <Text style={styles.errMeta}>{formatCost(data.total.costInput, cur)} · {inputShare}%</Text>
        </View>
        <View style={styles.errRow}>
          <Text style={styles.errName}>Sortie · {formatTokens(data.total.outputTokens)} tokens</Text>
          <Text style={styles.errMeta}>{formatCost(data.total.costOutput, cur)} · {100 - inputShare}%</Text>
        </View>
      </Section>

      <Section title="Coût par usage" testID="admin-ai-by-mode">
        {data.byMode.length === 0 ? (
          <Text style={styles.empty}>Aucun appel sur la période.</Text>
        ) : (
          data.byMode.map((m) => (
            <View key={m.mode} style={styles.errRow}>
              <Text style={styles.errName}>{AI_MODE_LABEL[m.mode] ?? m.mode}</Text>
              <Text style={styles.errMeta}>
                {formatCost(m.cost, cur)} · {m.calls} appel{m.calls > 1 ? 's' : ''}
              </Text>
            </View>
          ))
        )}
      </Section>

      <Section title="Évolution hebdomadaire" testID="admin-ai-weekly">
        {data.weekly.length === 0 ? (
          <Text style={styles.empty}>Aucun appel sur les 12 dernières semaines.</Text>
        ) : (
          data.weekly.map((w) => (
            <View key={w.week} style={styles.errRow}>
              <Text style={styles.errName}>{w.week}</Text>
              <Text style={styles.errMeta}>
                {formatTokens(w.tokens)} · {formatCost(w.cost, cur)}
              </Text>
            </View>
          ))
        )}
      </Section>
    </>
  );
}

// Deux sections : top 3 erreurs par feature + progression moyenne (mono-analyse exclus).
function MetricsSection({ data }: { data: AnalysisMetrics }) {
  return (
    <>
      <Section title="Erreurs les plus fréquentes" testID="admin-metrics-errors">
        <SubLabel>CV</SubLabel>
        <ErrorFreqList items={data.topErrors.cv} />
        <SubLabel>LinkedIn</SubLabel>
        <ErrorFreqList items={data.topErrors.linkedin} />
      </Section>
      <Section title="Progression moyenne" testID="admin-metrics-progression">
        <View style={styles.errRow}>
          <Text style={styles.errName}>CV (notation → CV amélioré)</Text>
          <Text style={styles.errMeta} testID="admin-metrics-prog-cv">
            {progressionText(data.progression.cv)}
          </Text>
        </View>
        <View style={styles.errRow}>
          <Text style={styles.errName}>LinkedIn (1er → dernier audit)</Text>
          <Text style={styles.errMeta} testID="admin-metrics-prog-linkedin">
            {progressionText(data.progression.linkedin)}
          </Text>
        </View>
      </Section>
    </>
  );
}

export function AdminScreen({ onBack }: AdminScreenProps) {
  const { window, setWindow, kpi, loading, error, forbidden } = useAdminKpi();

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader onBack={onBack} title="Dashboard admin" testID="admin-back" />

      <ScrollView contentContainerStyle={styles.content}>
        {/* Sélecteur de période */}
        <View style={styles.segmented}>
          {WINDOWS.map((w) => {
            const active = window === w;
            return (
              <Pressable
                key={w}
                testID={`admin-period-${w}`}
                accessibilityRole="button"
                accessibilityState={{ selected: active }}
                onPress={() => setWindow(w)}
                style={[styles.segment, active && styles.segmentActive]}
              >
                <Text style={[styles.segmentText, active && styles.segmentTextActive]}>
                  {WINDOW_LABEL[w]}
                </Text>
              </Pressable>
            );
          })}
        </View>

        {loading ? (
          <View testID="admin-loading" style={styles.center}>
            <ActivityIndicator color={colors.electric} />
          </View>
        ) : forbidden ? (
          <Text testID="admin-denied" style={styles.empty}>
            Accès réservé aux administrateurs.
          </Text>
        ) : error || !kpi ? (
          <Text testID="admin-error" style={styles.errorText}>
            {error ?? 'Aucune donnée.'}
          </Text>
        ) : (
          <>
            <Pressable testID="admin-export" onPress={() => void exportAdminPdf(kpi)} style={styles.export}>
              <Text style={styles.exportText}>Exporter en PDF</Text>
            </Pressable>

            {/* Limites assumées, affichées explicitement */}
            <Text testID="admin-limits" style={styles.limits}>
              Rétention basée sur les ouvertures réelles de l’app (1 par jour) : se remplit à partir du
              déploiement du tracking. Les distributions (âge, études, ville, domaine) reflètent l’état de la
              base, non filtré par période. Niveau d’études et ville sont déduits par l’IA à l’analyse d’un CV
              (couverture partielle ; « Non déterminé » si non inférable).
            </Text>

            <Section title="Utilisateurs" testID="admin-users">
              <View style={styles.tiles}>
                <StatTile value={kpi.users.total} label="Inscrits (total)" />
                <StatTile value={kpi.users.newInWindow} label="Nouveaux" />
                <StatTile value={kpi.users.active} label="Actifs (réels)" />
              </View>
              <SubLabel>Inscriptions par mois</SubLabel>
              <BarList items={kpi.users.signupSeries.map((p) => ({ label: p.month, count: p.count }))} />
              <SubLabel>Tranches d’âge</SubLabel>
              <BarList items={kpi.users.byAge} />
              <SubLabel>Niveau d’études (brut)</SubLabel>
              <BarList items={kpi.users.byEducation} />
              <SubLabel>Villes (brut)</SubLabel>
              <BarList items={kpi.users.byCity} />
              <SubLabel>Domaines email</SubLabel>
              <BarList items={kpi.users.byDomain} />
            </Section>

            <Section title="Rétention" testID="admin-retention">
              <View style={styles.tiles}>
                <StatTile value={kpi.retention.dau} label="DAU (jour)" />
                <StatTile value={kpi.retention.wau} label="WAU (7j)" />
                <StatTile value={kpi.retention.mau} label="MAU (30j)" />
                <StatTile value={`${kpi.retention.stickiness}%`} label="Stickiness" />
                <StatTile value={kpi.retention.avgActiveDays} label="Jours actifs / user" />
              </View>
              <SubLabel>Rétention par cohorte (semaine d’inscription → % actifs J+1..J+7)</SubLabel>
              <BarList
                items={kpi.retention.cohorts.map((c) => ({
                  label: `${c.week} (${c.size})`,
                  count: c.w1Retention,
                }))}
              />
            </Section>

            <Section title="Usage" testID="admin-usage">
              {kpi.usage.atsAvg !== null ? (
                <View style={styles.ringRow}>
                  <ScoreRing score={kpi.usage.atsAvg} testID="admin-ats" />
                  <Text style={styles.ringLabel}>Score ATS moyen</Text>
                </View>
              ) : null}
              <SubLabel>Volume par fonctionnalité</SubLabel>
              <BarList items={kpi.usage.byFeature} />
              <SubLabel>Distribution des scores ATS</SubLabel>
              <BarList items={kpi.usage.atsDistribution} />
            </Section>

            <Section title="Revenus" testID="admin-revenue">
              <View style={styles.tiles}>
                <StatTile value={kpi.revenue.coaching.count} label="Coaching (nb)" />
                <StatTile value={formatEuros(kpi.revenue.coaching.revenue)} label="CA coaching" />
                <StatTile value={formatEuros(kpi.revenue.subscriptions.mrr)} label="MRR" />
              </View>
              <SubLabel>Coaching par formule</SubLabel>
              <BarList items={kpi.revenue.coaching.byFormule} />
              <SubLabel>Abonnements par plan</SubLabel>
              <BarList items={kpi.revenue.subscriptions.byPlan} />
            </Section>

            <Section title="Erreurs (top 5)" testID="admin-errors">
              {kpi.errors.length === 0 ? (
                <Text style={styles.empty}>Aucune erreur sur la période.</Text>
              ) : (
                kpi.errors.map((e) => (
                  <View key={`${e.feature}-${e.errorType}`} style={styles.errRow}>
                    <Text style={styles.errName}>
                      {e.feature} · {e.errorType}
                    </Text>
                    <Text style={styles.errMeta}>
                      {e.count} occ. · {e.distinctUsers} utilisateurs
                    </Text>
                  </View>
                ))
              )}
            </Section>

            <Section title="Refus de rédiger (offre trop éloignée)" testID="admin-refusals">
              {(kpi.refusals ?? []).length === 0 ? (
                <Text style={styles.empty}>Aucun refus sur la période.</Text>
              ) : (
                (kpi.refusals ?? []).map((r) => (
                  <View key={r.feature} style={styles.errRow}>
                    <Text style={styles.errName}>{r.feature}</Text>
                    <Text style={styles.errMeta}>
                      {r.count} refus sur {r.tentatives} tentatives
                      {r.tentatives > 0 ? ` · ${Math.round((100 * r.count) / r.tentatives)} %` : ''}
                    </Text>
                  </View>
                ))
              )}
            </Section>

            <Section title="Reporting école" testID="admin-schools">
              {kpi.schools.length === 0 ? (
                <Text style={styles.empty}>Aucun établissement identifié (domaines grand public exclus).</Text>
              ) : (
                kpi.schools.map((s) => (
                  <View key={s.domain} style={styles.errRow}>
                    <Text style={styles.errName}>{s.domain}</Text>
                    <Text style={styles.errMeta}>
                      {s.students} élèves · {s.active} actifs · {s.cvsCreated} CV · ATS{' '}
                      {s.atsAvg ?? 'n/a'} · {s.interviews} entretiens
                    </Text>
                  </View>
                ))
              )}
            </Section>

            <FeedbackSection data={kpi.feedback} />

            {kpi.aiUsage ? <AiUsageSection data={kpi.aiUsage} /> : null}
            {kpi.apiCalls ? <ApiCallsSection data={kpi.apiCalls} /> : null}
            {kpi.metrics ? <MetricsSection data={kpi.metrics} /> : null}

            <Section title="Repères" testID="admin-reperes">
              {REPERES_ADMIN.map((r) => (
                <View key={r.date + r.texte} style={styles.errRow}>
                  <Text style={styles.errName}>{r.date}</Text>
                  <Text style={styles.errMeta}>{r.texte}</Text>
                </View>
              ))}
            </Section>
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: { padding: screenPadding, gap: spacing.lg, paddingBottom: spacing.xxl },
  center: { alignItems: 'center', marginTop: spacing.xl },

  segmented: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: colors.grey200,
    borderRadius: radius.md,
    backgroundColor: colors.white,
    padding: 3,
    gap: 3,
  },
  segment: { flex: 1, paddingVertical: spacing.sm, borderRadius: radius.sm, alignItems: 'center' },
  segmentActive: { backgroundColor: colors.electric },
  segmentText: { fontSize: typography.small.size, fontWeight: '600', color: colors.grey500 },
  segmentTextActive: { color: colors.white },

  export: {
    alignSelf: 'flex-start',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radius.pill,
    borderWidth: 1,
    borderColor: colors.electric,
  },
  exportText: { color: colors.electric, fontWeight: '700', fontSize: typography.small.size },

  limits: { fontSize: typography.small.size, color: colors.grey500, lineHeight: 18 },
  errorText: { color: colors.error, fontSize: typography.body.size },
  empty: { color: colors.grey400, fontSize: typography.small.size },
  aiNote: { color: colors.grey500, fontSize: typography.small.size, lineHeight: typography.small.lineHeight },
  aiWarn: { color: colors.warning, fontSize: typography.small.size, lineHeight: typography.small.lineHeight },

  section: { gap: spacing.sm },
  sectionTitle: { fontSize: typography.h2.size, fontWeight: typography.h2.weight, color: colors.night },
  subLabel: {
    fontSize: typography.caption.size,
    fontWeight: typography.caption.weight,
    letterSpacing: typography.caption.letterSpacing,
    color: colors.grey500,
    marginTop: spacing.sm,
  },

  tiles: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  tile: {
    flexGrow: 1,
    minWidth: 96,
    padding: spacing.md,
    borderRadius: radius.lg,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey200,
  },
  tileValue: { fontSize: typography.h1.size, fontWeight: typography.h1.weight, color: colors.night },
  tileLabel: { fontSize: typography.small.size, color: colors.grey500 },

  bars: { gap: spacing.xs },
  barRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  barLabel: { width: 96, fontSize: typography.small.size, color: colors.grey700 },
  barTrack: { flex: 1, height: 10, borderRadius: radius.sm, backgroundColor: colors.grey100, overflow: 'hidden' },
  barFill: { height: 10, borderRadius: radius.sm, backgroundColor: colors.electric },
  barValue: { width: 40, textAlign: 'right', fontSize: typography.small.size, fontWeight: '600', color: colors.grey900 },

  ringRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.lg },
  ringLabel: { fontSize: typography.body.size, color: colors.grey700 },

  errRow: { paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.grey100 },
  errName: { fontSize: typography.bodyMd.size, fontWeight: typography.bodyMd.weight, color: colors.grey900 },
  errMeta: { fontSize: typography.small.size, color: colors.grey500 },
  commentText: { fontSize: typography.small.size, color: colors.grey700, marginTop: 2 },
  moreBtn: {
    marginTop: spacing.sm,
    paddingVertical: spacing.sm,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.grey200,
    alignItems: 'center',
  },
  moreText: { fontSize: typography.small.size, fontWeight: '600', color: colors.electric },
});
